import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Stepper,
    Step,
    StepLabel,
    DialogActions,
    Grid,
    Typography,
    Box,
    StepConnector,
    Tooltip
} from '@mui/material'
import {SlideUp} from '@oc/shell-core'
import {makeOcStyles, withStyles} from '@oc/styles'
import {
    OcGenericStepDialogStateContextProvider,
    useOcGenericStepDialogStateContext
} from './OcGenericStepDialogStateContext'
import {OcButton} from '../BasicOcComponents/OcButton'
import {OcGenericStepDialogActionsProps, OcGenericStepDialogProps} from './OcGenericStepDialogInterfaces'
import {messages} from '../translations'
import {OcGenericStepDialogButtonEnum} from './OcGenericStepDialogEvents'

const useStyles = makeOcStyles(theme => ({
    dialogPaper: {
        minWidth: '95%',
        maxWidth: '95%',
        minHeight: '93vh',
        maxHeight: '93vh'
    },
    dialogBoxTitleRoot: {
        display: 'flex'
    },
    header: {
        paddingRight: theme.spacing(1)
    },
    dialogTitleOverride: {
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    stepperIconCompleted: {
        color: `${theme.syskronPalette.colors.P700} !important`
    },
    colorGrey: {
        color: theme.syskronPalette.colors.S500
    },
    colorBlack: {
        color: theme.syskronPalette.colors.S900
    },
    stepLabel: {
        fontWeight: 700,
        fontSize: '16px'
    },
    step: {
        maxWidth: '160px !important'
    },
    stepper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: theme.spacing(4)
    },
    actionButton: {
        width: '132px'
    },
    dialogActions: {
        paddingBottom: '24px',
        paddingRight: '24px'
    }
}))

const CustomStepConnector = withStyles(StepConnector, {
    root: {display: 'flex !important', justifyContent: 'center !important'},
    line: {width: '120px !important'}
})

function _OcGenericStepDialog(props: OcGenericStepDialogProps) {
    return (
        <OcGenericStepDialogStateContextProvider dialogId={props.dialogId}>
            <OcGenericStepDialogComponent {...props} />
        </OcGenericStepDialogStateContextProvider>
    )
}
export const OcGenericStepDialog = observer(_OcGenericStepDialog)

interface TooltipButtonWrapperProps extends React.PropsWithChildren {
    tooltip?: string
}

function _TooltipButtonWrapper({children, tooltip}: TooltipButtonWrapperProps) {
    return (
        <Tooltip title={tooltip || ''}>
            <span>{children}</span>
        </Tooltip>
    )
}
export const TooltipButtonWrapper = observer(_TooltipButtonWrapper)

function _OcGenericStepDialogActions(props: OcGenericStepDialogActionsProps) {
    const {state, onClose, onCloseLabel, onConfirmLabel, onConfirm, onCloseTooltip, onConfirmTooltip} = props
    const {classes} = useStyles()
    const step = state.currentStep
    const externalButtonSettings = state.externalButtonSettings
    const cancelSettings = externalButtonSettings.get(OcGenericStepDialogButtonEnum.cancel)
    const previousSettings = externalButtonSettings.get(OcGenericStepDialogButtonEnum.previous)
    const nextSettings = externalButtonSettings.get(OcGenericStepDialogButtonEnum.next)
    const confirmSettings = externalButtonSettings.get(OcGenericStepDialogButtonEnum.confirm)

    return (
        <DialogActions className={classes.dialogActions}>
            {(!step || step.showCancel) && !cancelSettings?.hidden ? (
                <TooltipButtonWrapper tooltip={onCloseTooltip}>
                    <OcButton
                        color="secondary"
                        data-cy="oc-generic-step-dialog-cancel-button"
                        onClick={onClose}
                        disabled={cancelSettings?.disabled}
                        className={classes.actionButton}
                    >
                        {onCloseLabel || messages.getText('genericStepDialog', 'cancel')}
                    </OcButton>
                </TooltipButtonWrapper>
            ) : null}
            {step && !state.isFirstStep && !step.hidePrevious && !previousSettings?.hidden ? (
                <TooltipButtonWrapper tooltip={step.previousButtonTooltip}>
                    <OcButton
                        data-cy="oc-generic-step-dialog-previous-button"
                        onClick={state.stepBackward}
                        disabled={previousSettings?.disabled}
                        color="secondary"
                        className={classes.actionButton}
                    >
                        {step.previousButtonLabel || messages.getText('genericStepDialog', 'previous')}
                    </OcButton>
                </TooltipButtonWrapper>
            ) : null}
            {step && state.isLastStep && !confirmSettings?.hidden ? (
                <TooltipButtonWrapper tooltip={onConfirmTooltip}>
                    <OcButton
                        data-cy="oc-generic-step-dialog-close-button"
                        disabled={!state.canGoNext || confirmSettings?.disabled}
                        onClick={onConfirm}
                        color="primary"
                        className={classes.actionButton}
                    >
                        {onConfirmLabel || messages.getText('genericStepDialog', 'confirm')}
                    </OcButton>
                </TooltipButtonWrapper>
            ) : null}
            {step && !state.isLastStep && !step.hideNext && !nextSettings?.hidden ? (
                <TooltipButtonWrapper tooltip={step.nextButtonTooltip}>
                    <OcButton
                        data-cy="oc-generic-step-dialog-next-button"
                        disabled={!state.canGoNext || nextSettings?.disabled}
                        onClick={state.stepForward}
                        color="primary"
                        className={classes.actionButton}
                    >
                        {step.nextButtonLabel || messages.getText('genericStepDialog', 'next')}
                    </OcButton>
                </TooltipButtonWrapper>
            ) : null}
        </DialogActions>
    )
}
export const OcGenericStepDialogActions = observer(_OcGenericStepDialogActions)

function _OcGenericStepDialogComponent(props: OcGenericStepDialogProps) {
    const {classes, cx} = useStyles()
    const state = useOcGenericStepDialogStateContext()
    const {open, onClose, steps, options, onConfirm, onCloseLabel, onConfirmLabel} = props

    const stepComponent = state.currentStep?.component

    const onDialogEnter = React.useCallback(() => {
        state.updateStepsConfiguration(steps, 0, false, true)
    }, [state, steps])

    return (
        <Dialog
            TransitionComponent={SlideUp}
            TransitionProps={{
                onEnter: onDialogEnter
            }}
            keepMounted={false}
            classes={{paper: classes.dialogPaper}}
            fullScreen={true}
            open={open}
        >
            <DialogTitle classes={{root: classes.dialogTitleOverride}}>
                <Box className={classes.dialogBoxTitleRoot}>
                    <Typography variant="h4" className={classes.header}>
                        {options?.header}
                    </Typography>
                    <Typography>{options?.secondaryHeader && `(${options.secondaryHeader})`}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <Stepper
                            alternativeLabel
                            activeStep={state.currentStepIndex}
                            connector={<CustomStepConnector />}
                            className={classes.stepper}
                        >
                            {state.stepLabels.map((name, idx) => (
                                <Step key={`step-${idx}`} className={classes.step}>
                                    <StepLabel
                                        slotProps={{
                                            stepIcon: {
                                                classes: {
                                                    completed: classes.stepperIconCompleted
                                                }
                                            }
                                        }}
                                    >
                                        <Typography
                                            className={
                                                idx > state.currentStepIndex
                                                    ? cx(classes.stepLabel, classes.colorGrey)
                                                    : cx(classes.stepLabel, classes.colorBlack)
                                            }
                                        >
                                            {name}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={12}>
                        {typeof stepComponent !== 'undefined' ? stepComponent() : null}
                    </Grid>
                </Grid>
            </DialogContent>
            <OcGenericStepDialogActions
                state={state}
                onClose={onClose}
                onCloseLabel={onCloseLabel}
                onConfirmLabel={onConfirmLabel}
                onConfirm={onConfirm}
            />
        </Dialog>
    )
}
export const OcGenericStepDialogComponent = observer(_OcGenericStepDialogComponent)
